import { MessageCenter } from "imagica-corekit/dist/base/kernel/MessageCenter";
import { useSignal } from "@preact/signals-react";
import { getIt } from "@uikit/getIt";
import { BrainClient } from "imagica-corekit/dist/base/api/BrainClient";
import { RestResponse } from "imagica-corekit/dist/base/cutil/RestClient";
import { logEvent } from "@uikit/service/amplitude";
import func from "@uikit/func";
import { ErrorUtil } from "imagica-corekit/dist/base/api/ErrorUtil";

const INDEXERS_IS_READY = "INDEXERS_IS_READY";

type chatHistoryType = {
  role: string;
  content: string;
  additional_kwargs: object;
};

export type ChatUserType = "Assistant" | "User" | "Error";

export type ChatType = {
  type: ChatUserType; // Assistant | User | Error
  value: string;
};
const ERRORTIP = "Either the engine you requested does not exist or there was another issue processing your request.";

const defaultErrorChatObj: ChatType = {
  type: "Error",
  value: ERRORTIP,
};
const AssistantDefaultList = ["Hello, I am an AI trained to answer questions about ", "How can I help you?"];

export class ChatInterfaceBloc {
  brainClient = getIt(BrainClient);
  inputText = useSignal<string>("");
  file = useSignal<string>("");
  file_uuid = useSignal<string | undefined>(undefined);
  filename = useSignal<string>("");
  prevInput = useSignal<string>("");
  isLoading = useSignal<boolean>(false);
  isMessageLoading = useSignal<boolean>(false);
  chatList = useSignal<Array<ChatType>>([]);
  isStreamStopped = useSignal<boolean>(true);
  chatHistoryVarible = useSignal<Array<chatHistoryType>>([]);
  AssistantDefaultList = useSignal<Array<string>>(AssistantDefaultList);
  textAreaValueChatList = useSignal<Array<ChatType>>([]);
  messageCenter = getIt(MessageCenter);
  isIndexers = useSignal<boolean>(false);
  pubsub_topic = "";

  init(props: any, isShare: boolean, isInputFoucus: boolean) {
    if (func.isEmpty(props.textAreaValue?.file)) return;
    this.file.value = props.textAreaValue.file;
    this.pubsub_topic = props.textAreaValue.pubsub_topic;
    this.file_uuid.value = props.textAreaValue.file_uuid;

    this.AssistantDefaultList.value = this.getAssistantDefaultList(props.textAreaValue.filename);
    if (!isShare && !func.isEmpty(props.textAreaValue.chatList)) {
      this.chatList.value = props.textAreaValue.chatList || [];
      this.prevInput.value = props.textAreaValue.preQuery || "";
      this.chatHistoryVarible.value = props.textAreaValue.chatHistory || [];
      this.textAreaValueChatList.value = props.textAreaValue.chatList || [];
      this.isStreamStopped.value = props.textAreaValue.isStreamStopped;
    } else {
      this.prevInput.value = "";
      if (!isInputFoucus) {
        /** 排除掉输入换行执行到这儿 导致文本被清空的问题*/
        this.inputText.value = "";
      }
      this.chatHistoryVarible.value = [];
      this.chatList.value = [];
      this.isLoading.value = false;
      this.textAreaValueChatList.value = [];
    }
  }

  getAssistantDefaultList(filename: string) {
    this.AssistantDefaultList.value = AssistantDefaultList;
    const arr = [...this.AssistantDefaultList.value];
    arr[0] = arr[0] + filename + ".";
    return arr;
  }

  async postIndexers(query: string): Promise<RestResponse<any>> {
    const isIndexers = this.isIndexers.value;
    try {
      return await this.brainClient.openAi.indexers({
        mode: "chat",
        data: {
          file: this.file.value,
          query,
          loader: "PDFReader",
        },
        chat_history: !func.isEmpty(this.chatHistoryVarible.value) ? this.chatHistoryVarible.value : undefined,
        uuid: this.file_uuid.value,
      });
    } catch (error: any) {
      if (isIndexers === true) {
        return Promise.resolve(error);
      } else {
        return new Promise(() => {});
      }
    }
  }

  stockRequest() {
    return new Promise(async resolve => {
      if (this.isIndexers.value === false) {
        // Wait for websocketResponse
        const timer = setInterval(() => {
          if (this.isIndexers.value === true) {
            clearInterval(timer);
            resolve(INDEXERS_IS_READY);
          }
        }, 2000);
      }
    });
  }

  pollingRequest(query: string): Promise<any> {
    return Promise.race([this.stockRequest(), this.postIndexers(query)]).then(async res => {
      if (res === INDEXERS_IS_READY) {
        return await this.postIndexers(query);
      }
      return res;
    });
  }

  async onPressEnter() {
    if (func.isEmpty(this.inputText.value) || this.isMessageLoading.value) {
      return;
    }
    this.isMessageLoading.value = true;
    logEvent("chat_user_input", { chat_user_input: this.inputText.value });
    try {
      this.chatList.value = [
        ...this.chatList.value,
        {
          type: "User",
          value: this.inputText.value,
        },
      ];
      this.prevInput.value = this.inputText.value;
      const result: RestResponse<any> = await this.pollingRequest(this.inputText.value);

      this.inputText.value = "";
      if (result.isSuccess === false) {
        throw result.errorDescription;
      }

      this.chatHistoryVarible.value = result.data.data.chat_history;
      logEvent("chat_ai_response", { chat_ai_response: result.data.data.response });
      this.chatList.value = [
        ...this.chatList.value,
        {
          type: "Assistant",
          value: result.data.data.response,
        },
      ];
      /// 添加数据后，开始数据流动画
      this.isStreamStopped.value = false;
    } catch (error: any) {
      const errorText = typeof error === "string" ? error : ErrorUtil.defaultErrorMsg;
      this.chatList.value = [...this.chatList.value, { ...defaultErrorChatObj, value: errorText }];
    }
    this.isMessageLoading.value = false;
  }

  async onRegenerate() {
    if (func.isEmpty(this.chatList.value)) {
      return;
    }
    try {
      const arr = [...this.chatList.value];
      const last = arr.pop();
      if (last) {
        logEvent("click_chat_regenerate", { chat_regenerate: last.value });
      }
      this.chatList.value = arr;
      this.isMessageLoading.value = true;
      const result = await this.postIndexers(this.prevInput.value);
      if (result.isSuccess === false) {
        throw result.errorDescription;
      }
      const newObj: ChatType = {
        type: "Assistant",
        value: result.data.data.response,
      };
      this.chatList.value = [...this.chatList.value, newObj];
      /// 添加数据后，开始数据流动画
      this.isStreamStopped.value = false;

      logEvent("chat_ai_response", { chat_ai_response: result.data.data.response });
    } catch (error: any) {
      const errorText = typeof error === "string" ? error : ErrorUtil.defaultErrorMsg;
      this.chatList.value = [...this.chatList.value, { ...defaultErrorChatObj, value: errorText }];
    } finally {
      this.isMessageLoading.value = false;
    }
  }

  onReStart() {
    logEvent("click_chat_start_new");
    this.prevInput.value = "";
    this.chatHistoryVarible.value = [];
    this.chatList.value = [];
  }

  onLinkClicked() {
    window.open("https://imagica.zendesk.com/hc/en-us/articles/18399149126171-Imagica-Terms-of-Use");
  }

  listenTopic() {
    this.messageCenter.on(this.pubsub_topic, (res: any) => {
      this.isIndexers.value = true;
      this.messageCenter.off(this.pubsub_topic);
    });
  }
}
